/**
 * Cascade-aware delete confirmation. Wraps the worker's
 * `/cascade-preview` endpoint with a friendly NL/EN message and a
 * `window.confirm` UX — minimal-chrome but still informative.
 *
 * Why not a custom modal? A real React dialog is on the M2 wishlist
 * but the data here (counts + a short list) reads cleanly enough in
 * the browser-native confirm prompt, so we ship that first and can
 * upgrade the UX later without changing call-sites.
 */
import type { BilingualText } from "@ae/domain";
import i18n from "i18next";
import { api } from "../api/client";
import { getRegistry, resolveBilingual } from "../registry";
import { useWorkspaceData } from "../state/workspace";
import { confirmDialog } from "./confirmDialog";

interface ConfirmHardDeleteArgs {
  workspaceId: string;
  branchId: string;
  conceptIds: string[];
  /** UI language for human-readable diagram names. */
  lang: "nl" | "en";
  /**
   * Number of edges (relations) the user explicitly selected on the
   * canvas alongside the concepts. Counted separately because they
   * disappear too.
   */
  standaloneEdgeCount: number;
  /** Optional name lookup so the prompt mentions the concept by name. */
  conceptName?: (id: string) => BilingualText | undefined;
  /** Zelfstandig naamwoord voor het te verwijderen object (single-delete).
   *  Default "Concept"; voor Sharp-rijen "Attribuut" / "Identifier". */
  kindLabel?: string;
}

/**
 * Fetch cascade-impact for each concept, build a human-readable
 * summary, and ask the user to confirm. Returns true when confirmed.
 *
 * On network failure we fall back to a plain message so the user can
 * still proceed (or cancel) — never silently block the delete just
 * because the preview endpoint hiccuped.
 */
export async function confirmHardDelete(args: ConfirmHardDeleteArgs): Promise<boolean> {
  const { workspaceId, branchId, conceptIds, lang, standaloneEdgeCount, conceptName } = args;
  const store = useWorkspaceData.getState().store;
  const reg = getRegistry();

  // Facet-referentie-blokkade (§12.3): een object dat als facet-referentie wordt
  // gebruikt door een «sharp.Concept» mag niet verwijderd worden. Proactieve
  // reverse-lookup zodat de modelleur de bron-concepten ziet i.p.v. een
  // generieke delete-prompt gevolgd door een 409. Backstop: worker-guard.
  const allConcepts = store.listConcepts();
  // Type-label voor tussen blokhaken: stereotype-label wint (bv. «Concept»),
  // anders het element-type-label, anders de rauwe typeId.
  const typeLabelFor = (cc: { typeId: string; stereotypes: string[] }): string => {
    for (const sid of cc.stereotypes) {
      const st = reg.getStereotype(sid);
      if (st) return resolveBilingual(st.label, lang);
    }
    const et = reg.getElementType(cc.typeId);
    return et ? resolveBilingual(et.label, lang) : cc.typeId;
  };
  const blockLines: string[] = [];
  for (const delId of conceptIds) {
    const referencedBy: string[] = [];
    for (const cc of allConcepts) {
      const fr = (cc.attributes as Record<string, unknown> | undefined)?.facet_references;
      if (Array.isArray(fr) && fr.includes(delId)) {
        const name = resolveBilingual(cc.name, lang) || cc.id;
        referencedBy.push(`${name} [${typeLabelFor(cc)}]`);
      }
    }
    if (referencedBy.length > 0) {
      const nm = conceptName?.(delId);
      const label = nm ? resolveBilingual(nm, lang) || delId : delId;
      blockLines.push(i18n.t("cascadeConfirm.blockedByFacet", { name: label }));
      for (const r of referencedBy) blockLines.push(`  • ${r}`);
    }
  }
  if (blockLines.length > 0) {
    blockLines.push("");
    blockLines.push(i18n.t("cascadeConfirm.removeFacetsFirst"));
    window.alert(blockLines.join("\n"));
    return false;
  }

  // Zelfstandig naamwoord voor de prompt: expliciete kindLabel wint; anders leid
  // 'm af van het type (stereotype-label of element-type-label) zodat een
  // BusinessActor niet "Concept" heet.
  let kindLabel = args.kindLabel;
  if (!kindLabel && conceptIds.length === 1) {
    const only = store.getConcept(conceptIds[0]!);
    if (only) {
      for (const sid of only.stereotypes) {
        const st = reg.getStereotype(sid);
        if (st) {
          kindLabel = resolveBilingual(st.label, lang);
          break;
        }
      }
      if (!kindLabel) {
        const et = reg.getElementType(only.typeId);
        if (et) kindLabel = resolveBilingual(et.label, lang);
      }
    }
  }
  kindLabel = kindLabel ?? i18n.t("cascadeConfirm.object");

  const previews = await Promise.allSettled(
    conceptIds.map((id) => api.cascadePreviewConcept(workspaceId, branchId, id)),
  );

  let _totalDiagrams = 0;
  let totalRelations = 0;
  const diagramNames = new Set<string>();
  let anyFailed = false;
  for (const p of previews) {
    if (p.status === "fulfilled") {
      _totalDiagrams += p.value.summary.diagram_count;
      totalRelations += p.value.summary.relation_count;
      for (const d of p.value.diagrams) {
        const name = resolveBilingual(d.name, lang) || d.id;
        diagramNames.add(name);
      }
    } else {
      anyFailed = true;
    }
  }

  const lines: string[] = [];
  if (conceptIds.length === 0) {
    // Alleen relaties geselecteerd — geen "0 concepten …"-kop.
    lines.push(i18n.t("cascadeConfirm.titleRelations", { count: standaloneEdgeCount }));
  } else if (conceptIds.length === 1) {
    const id = conceptIds[0]!;
    const name = conceptName?.(id);
    lines.push(
      i18n.t("cascadeConfirm.titleOne", {
        kind: kindLabel,
        name: name ? resolveBilingual(name, lang) || id : id,
      }),
    );
  } else {
    lines.push(i18n.t("cascadeConfirm.titleMany", { count: conceptIds.length }));
  }
  if (totalRelations > 0) {
    lines.push(i18n.t("cascadeConfirm.relationsGo", { count: totalRelations }));
  }
  if (standaloneEdgeCount > 0 && conceptIds.length > 0) {
    lines.push(i18n.t("cascadeConfirm.standaloneGo", { count: standaloneEdgeCount }));
  }
  if (diagramNames.size > 0) {
    const names = [...diagramNames];
    const list = names.length > 5 ? `${names.slice(0, 5).join(", ")}, …` : names.join(", ");
    lines.push(i18n.t("cascadeConfirm.onDiagrams", { count: diagramNames.size, list }));
  }
  if (anyFailed) {
    lines.push(i18n.t("cascadeConfirm.previewFailed"));
  }
  lines.push("");
  // Niet "onomkeerbaar": verwijderde objecten en relaties staan daarna in de
  // prullenbak, en wie mag schrijven zet ze daar terug.
  lines.push(i18n.t("cascadeConfirm.toTrash"));

  return confirmDialog({
    message: lines.join("\n"),
    okLabel: i18n.t("cascadeConfirm.ok"),
    danger: true,
  });
}
